import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTick } from "./shared";

const POOL = [
  ["INFO", "AP-03 client roamed to 6GHz", "A laptop switched to a faster Wi-Fi band on its own."],
  ["WARN", "WAN1 latency 48ms · site Branch A", "Branch A's internet is slowing — we're already looking into it."],
  ["CRIT", "IPS blocked 185.220.101.4 · ET SCAN", "A threat was blocked automatically — we saw it before you did."],
  ["INFO", "Firmware 4.1.13 staged · 12 devices", "Updates are scheduled overnight, so nothing breaks during work hours."],
  ["WARN", "Failed login x5 · VPN user jsmith", "Someone keeps getting a VPN password wrong — we'll check it's really them."],
  ["INFO", "Backup completed · UDM Pro HQ", "Your network settings were backed up automatically."],
  ["CRIT", "Geo-IP block · 3 sessions RU/CN", "Connections from high-risk countries were blocked."],
  ["INFO", "New device onboarded · G5 Turret", "A new camera was added and secured automatically."],
];
const SEV = { INFO: "text-cyan-300 border-cyan-300/30", WARN: "text-amber-400 border-amber-400/30", CRIT: "text-rose-400 border-rose-400/30" };
const stamp = () => new Date().toTimeString().slice(0, 8);

const useLive = () => {
  const [pts, setPts] = useState(() => Array.from({ length: 40 }, () => 40 + Math.random() * 30));
  const [log, setLog] = useState(() => POOL.slice(0, 4).map((e, i) => ({ id: i, t: stamp(), e })));
  const k = useRef(100);
  useTick(() => setPts((p) => [...p.slice(1), 30 + Math.random() * 50]), 500);
  useTick(() => setLog((l) => [{ id: k.current++, t: stamp(), e: POOL[Math.floor(Math.random() * POOL.length)] }, ...l].slice(0, 4)), 1400);
  return { pts, log };
};

const Tile = ({ k, v, c = "text-white" }) => (
  <div className="border border-white/10 bg-white/[0.02] px-3 py-2">
    <p className="font-mono text-[8px] tracking-[0.2em] text-slate-500">{k}</p>
    <p className={`mt-1 font-display text-xl font-bold ${c}`}>{v}</p>
  </div>
);

export const NocAnim = ({ say }) => {
  const { pts, log } = useLive();
  useEffect(() => say(log[0].e[2]), [log, say]);
  const line = pts.map((p, i) => `${(i / 39) * 300},${100 - p}`).join(" ");
  return (
    <div className="h-full w-full p-3 sm:p-4 flex flex-col gap-2.5 text-left" data-testid="noc-anim">
      <div className="grid grid-cols-4 gap-2">
        <Tile k="SITES" v="12" />
        <Tile k="DEVICES" v="318" />
        <Tile k="UPTIME" v="99.98%" c="text-emerald-400" />
        <Tile k="OPEN ALERTS" v={log.filter((l) => l.e[0] === "CRIT").length} c="text-rose-400" />
      </div>
      <div className="grid grid-cols-3 gap-2 flex-1 min-h-0">
        <div className="col-span-2 border border-white/10 bg-white/[0.02] p-3 flex flex-col min-h-0">
          <p className="font-mono text-[8px] tracking-[0.2em] text-slate-500">WAN THROUGHPUT · ALL SITES</p>
          <svg viewBox="0 0 300 100" preserveAspectRatio="none" className="mt-2 flex-1 min-h-0 w-full">
            <polyline points={`0,100 ${line} 300,100`} fill="rgba(0,240,255,0.08)" stroke="none" />
            <polyline points={line} fill="none" stroke="#00F0FF" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-3 flex flex-col items-center min-h-0">
          <p className="self-start font-mono text-[8px] tracking-[0.2em] text-slate-500">THREAT RADAR</p>
          <div className="relative mt-2 aspect-square w-full max-w-[110px] rounded-full border border-emerald-400/30 overflow-hidden">
            <div className="absolute inset-[25%] rounded-full border border-emerald-400/20" />
            <div className="absolute inset-0 radar-sweep" />
            <span className="absolute left-[30%] top-[35%] h-1.5 w-1.5 rounded-full bg-rose-400 blink" />
            <span className="absolute left-[62%] top-[60%] h-1.5 w-1.5 rounded-full bg-amber-400 blink" style={{ animationDelay: "0.6s" }} />
          </div>
        </div>
      </div>
      <div className="border border-white/10 bg-white/[0.02] p-3 h-[124px] shrink-0 overflow-hidden">
        <p className="font-mono text-[8px] tracking-[0.2em] text-slate-500 mb-2">SIEM · EVENT STREAM</p>
        <AnimatePresence initial={false} mode="popLayout">
          {log.map((l) => (
            <motion.div key={l.id} layout initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-2 font-mono text-[9px] sm:text-[10px] leading-5 text-slate-400 whitespace-nowrap">
              <span className="text-slate-600">{l.t}</span>
              <span className={`border px-1 ${SEV[l.e[0]]}`}>{l.e[0]}</span>
              <span className="truncate">{l.e[1]}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
